import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Phone } from "lucide-react";
import { BRAND, SERVICES } from "@/lib/site";
import ServiceIcon from "@/components/site/ServiceIcon";
import Seo from "@/components/site/Seo";

const BENEFITS = [
  "Fully customized to your loved one's routine",
  "Flexible scheduling from a few hours to 24/7 care",
  "Consistent, matched caregivers — not a rotating stranger",
  "Single point of contact for your family",
];

const SERVICES_JSONLD = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    {"@type": "ListItem", "position": 1, "name": "Home", "item": "https://yourelitehomecare.com/"},
    {"@type": "ListItem", "position": 2, "name": "Services", "item": "https://yourelitehomecare.com/services"}
  ]
};

export default function Services() {
  return (
    <div data-testid="page-services">
      <Seo
        path="/services"
        title="In-Home Senior Care Services — Personal, Companion, Dementia & More"
        description="Explore our full range of in-home senior care in Southwest Florida: personal care, companion care, medication reminders, meal preparation, light housekeeping, transportation, and Alzheimer's & dementia care. Serving Naples, Fort Myers, Cape Coral & Bonita Springs."
        jsonLd={SERVICES_JSONLD}
      />
      {/* Header */}
      <section className="relative isolate bg-navy-900 text-white overflow-hidden">
        <div
          aria-hidden
          className="absolute -top-24 -right-24 h-80 w-80 rounded-full bg-gold-400/15 blur-3xl"
        />
        <div className="container-x pt-20 pb-24 md:pt-28 md:pb-32">
          <span className="text-gold-300 text-xs uppercase tracking-[0.28em] font-medium">
            Our services
          </span>
          <h1 className="mt-5 font-serif text-5xl md:text-6xl max-w-3xl leading-[1.05] text-white">
            Care that fits the life your loved one has built.
          </h1>
          <p className="mt-6 max-w-2xl text-white/80 text-lg leading-relaxed">
            From a few hours of companionship to full-time specialized support,
            every in-home care plan is tailored, gentle, and delivered by
            caregivers we'd trust with our own families — proudly serving
            Naples, Fort Myers, Cape Coral, Bonita Springs, and all of
            Southwest Florida.
          </p>
          <div className="mt-9 flex flex-wrap gap-4">
            <Link to="/contact" data-testid="services-cta-consultation" className="btn-primary">
              {BRAND.cta.primary} <ArrowRight className="h-4 w-4" />
            </Link>
            <a href={BRAND.phoneHref} data-testid="services-cta-call" className="btn-ghost-light">
              <Phone className="h-4 w-4" strokeWidth={1.75} /> {BRAND.phone}
            </a>
          </div>
        </div>
      </section>

      {/* Full service list */}
      <section className="section">
        <div className="container-x grid grid-cols-1 lg:grid-cols-3 gap-6">
          {SERVICES.map((s) => (
            <Link
              key={s.slug}
              to={`/services/${s.slug}`}
              data-testid={`services-card-${s.slug}`}
              className="card-soft p-8 md:p-10 group block"
            >
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-navy-900 text-gold-300 group-hover:bg-gold-400 group-hover:text-navy-900 transition-colors">
                <ServiceIcon name={s.icon} className="h-6 w-6" />
              </div>
              <h2 className="mt-6 font-serif text-2xl md:text-3xl text-navy-900">
                {s.title}
              </h2>
              <p className="mt-1 text-sm uppercase tracking-[0.2em] text-gold-500">
                {s.short}
              </p>
              <p className="mt-4 text-navy-600 leading-relaxed">
                {s.description}
              </p>
              <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-navy-800 group-hover:text-gold-500 transition-colors">
                Learn more <ArrowRight className="h-3.5 w-3.5" />
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* Why us */}
      <section className="section bg-navy-50/60 border-y border-navy-900/5">
        <div className="container-x grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div>
            <span className="overline">Why families choose us</span>
            <h2 className="text-4xl md:text-5xl leading-tight">
              A higher standard of{" "}
              <span className="italic text-gold-500">in-home care.</span>
            </h2>
            <p className="mt-5 text-navy-600 text-lg leading-relaxed">
              You want more than a service — you want peace of mind. Every
              detail of how we hire, match, and support our caregivers is
              designed to deliver exactly that.
            </p>
            <ul className="mt-8 space-y-4">
              {BENEFITS.map((b) => (
                <li key={b} className="flex items-start gap-3 text-navy-800">
                  <CheckCircle2 className="h-5 w-5 text-gold-500 mt-0.5 shrink-0" strokeWidth={1.6} />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <Link
              to="/contact"
              data-testid="services-why-cta"
              className="btn-primary mt-10"
            >
              Build my care plan <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="relative">
            <img
              src="https://images.pexels.com/photos/8088906/pexels-photo-8088906.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              alt="Caregiver with an elderly client"
              className="w-full rounded-2xl object-cover aspect-[4/5] shadow-xl"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
